import React, { useRef } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { CheckCircle2, Camera, XCircle, Loader2 } from 'lucide-react';

/**
 * Dialog for admin to validate a task without requiring minimum photos.
 */
const AdminValidateDialog = ({
  open,
  onOpenChange,
  task,
  uploadedPhotos,
  adminPhotos,
  setAdminPhotos,
  adminPhotosPreviews,
  setAdminPhotosPreviews,
  onPhotoSelect,
  onValidate,
  actionLoading,
  adminPhotoUploading,
  arrivalTime,
  setArrivalTime,
  departureTime,
  setDepartureTime
}) => {
  const photoInputRef = useRef(null);

  const removePhoto = (idx) => {
    const newPhotos = [...adminPhotos];
    newPhotos.splice(idx, 1);
    setAdminPhotos(newPhotos);
    
    const newPreviews = [...adminPhotosPreviews];
    newPreviews.splice(idx, 1);
    setAdminPhotosPreviews(newPreviews);
  };

  const handleClose = () => {
    onOpenChange(false);
    setAdminPhotos([]);
    setAdminPhotosPreviews([]);
    if (setArrivalTime) setArrivalTime('');
    if (setDepartureTime) setDepartureTime('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" />
            Valider la prestation (Admin)
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-700">
              ⚠️ Cette action validera la prestation sans vérification des photos minimum. 
              Vous pouvez ajouter des photos ci-dessous si nécessaire.
            </p>
          </div>
          
          {/* Current photos count */}
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-sm text-slate-600">
              <strong>Photos actuelles :</strong> {uploadedPhotos?.length || 0} photo(s)
            </p>
            {task?.status && (
              <p className="text-sm text-slate-600 mt-1">
                <strong>Statut actuel :</strong> {task.status}
              </p>
            )}
          </div>

          {/* Heures arrivée / départ manuelles */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
            <p className="text-sm font-semibold text-blue-900">
              🕓 Heures manuelles (optionnel)
            </p>
            <p className="text-xs text-blue-700 -mt-1">
              Si renseignées, les photos que vous ajoutez seront horodatées à l'heure de départ.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-700 mb-1 block">Heure d'arrivée</Label>
                <input
                  type="time"
                  value={arrivalTime || ''}
                  onChange={(e) => setArrivalTime && setArrivalTime(e.target.value)}
                  className="w-full px-2 py-2 text-sm border border-slate-300 rounded-md bg-white"
                  data-testid="admin-arrival-time-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-700 mb-1 block">Heure de départ</Label>
                <input
                  type="time"
                  value={departureTime || ''}
                  onChange={(e) => setDepartureTime && setDepartureTime(e.target.value)}
                  className="w-full px-2 py-2 text-sm border border-slate-300 rounded-md bg-white"
                  data-testid="admin-departure-time-input"
                />
              </div>
            </div>
          </div>
          
          {/* Admin photo upload */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Ajouter des photos (optionnel)
            </Label>
            
            {adminPhotosPreviews.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {adminPhotosPreviews.map((preview, idx) => (
                  <div key={`admin-preview-${idx}`} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={preview} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                    <button 
                      type="button" 
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-blue-300 rounded-xl cursor-pointer hover:bg-blue-50 transition-colors bg-white"
              data-testid="admin-photo-select-btn"
            >
              <Camera className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-blue-600 font-medium">
                {adminPhotos.length > 0 ? `${adminPhotos.length} photo(s) sélectionnée(s)` : 'Sélectionner des photos'}
              </span>
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                onPhotoSelect(e);
                // Reset to allow re-selecting the same file
                if (e.target) e.target.value = '';
              }}
            />
          </div>
        </div>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={handleClose} className="sm:flex-1">
            Annuler
          </Button>
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700 sm:flex-1" 
            onClick={onValidate} 
            disabled={actionLoading}
            data-testid="confirm-admin-validate-btn"
          >
            {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {adminPhotoUploading ? 'Upload des photos...' : 'Valider la prestation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminValidateDialog;
